import { useAdminGerarCodigoPareamentoMutation } from '../../api'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Alert, AlertTitle, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Snackbar, Stack, Step, StepContent, StepLabel, Stepper, Typography, useMediaQuery, useTheme, } from '@mui/material'
import { ROTULO_TIPO_IDENTIFICADOR } from './icone-modo'
import type { IdentificadorLoja } from '../../api'
import { CampoCopiavel } from './campo-copiavel'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import RefreshIcon from '@mui/icons-material/Refresh'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows'
import AndroidIcon from '@mui/icons-material/Android'
import { BotaoPlataforma } from './botao-plataforma'
import { DialogTitle } from '../dialog-componentes'

const URL_DOWNLOADS = 'https://downloads.sischef.com/'

/** Aparelho que vai virar o totem: a instalação muda, o pareamento é o mesmo. */
type PlataformaTotem = 'WINDOWS' | 'ANDROID'

const ROTULO_PLATAFORMA: Record<PlataformaTotem, string> = {
  WINDOWS: 'Totem Windows',
  ANDROID: 'Totem Android',
}

function formatarCodigo(codigo: string): string {
  return `${codigo.slice(0, 4)} ${codigo.slice(4)}`
}

function formatarRestante(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const min = Math.floor(total / 60)
  const seg = total % 60

  return `${min}:${String(seg).padStart(2, '0')}`
}

function Caminho({ children }: { children: ReactNode }): JSX.Element {
  return <strong>{children}</strong>
}

interface Passo {
  titulo: string
  corpo: ReactNode
}

export function DialogParearDispositivo({ identificador, aoFechar, }: { identificador: IdentificadorLoja | null; aoFechar: () => void }): JSX.Element {
  const theme = useTheme()
  const telaEstreita = useMediaQuery(theme.breakpoints.down('sm'))
  const [gerar, { data, isLoading, error, reset }] = useAdminGerarCodigoPareamentoMutation()
  const [copiado, setCopiado] = useState(false)
  /** Aparelho escolhido — `null` enquanto a pergunta está na tela. Fechar volta para ela. */
  const [plataforma, setPlataforma] = useState<PlataformaTotem | null>(null)
  const [restanteMs, setRestanteMs] = useState(0)
  const aberto = !!identificador
  const expirado = !!data && restanteMs <= 0
  const idAlvo = identificador?.id ?? null
  const jaGeradoPara = useRef<number | null>(null)

  // O código só nasce depois da escolha do aparelho: ele vale UMA vez e expira
  // em minutos, então gerar antes gastaria um código de quem só abriu para ler.
  const podeGerar = plataforma !== null

  useEffect(() => {
    if (idAlvo == null) {
      jaGeradoPara.current = null
      return
    }

    if (!podeGerar) return

    if (jaGeradoPara.current === idAlvo) return

    jaGeradoPara.current = idAlvo
    void gerar(idAlvo)
  }, [idAlvo, podeGerar, gerar])

  useEffect(() => {
    if (aberto) return

    reset()
    setPlataforma(null)
  }, [aberto, reset])

  useEffect(() => {
    if (!data) return

    const alvo = new Date(data.dataExpiracao).getTime()
    const tick = (): void => setRestanteMs(alvo - Date.now())

    tick()

    const intervalo = setInterval(tick, 1000)
    return () => clearInterval(intervalo)
  }, [data])

  async function copiar(): Promise<void> {
    if (!data) return

    try {
      await navigator.clipboard.writeText(data.codigo)
      setCopiado(true)
    } catch { }
  }

  const ehCheckout = identificador?.tipo === 'TOTEM_CHECKOUT'

  const passoCodigo: Passo = {
    titulo: 'Digitar o código no totem',
    corpo: (
      <>
        Informe o código abaixo no teclado da tela do totem. Ele vale <strong>uma vez só</strong> e expira em
        poucos minutos se passar do tempo, gere outro aqui.
        <Box sx={{ mt: 1.5 }}>{blocoCodigo()}</Box>
      </>
    ),
  }

  const passoPronto: Passo = {
    titulo: 'Pronto, o aparelho fica vinculado',
    corpo: (
      <>
        A partir daí o totem abre direto neste ponto de acesso, inclusive depois de reiniciar. Para trocar de
        ponto de acesso depois, use no próprio aparelho: <Caminho>Sobre</Caminho> →{' '}
        <Caminho>Configurações</Caminho> → <Caminho>Trocar ponto de acesso</Caminho> (pede o PIN da loja configurado na aba <Caminho>Gerais</Caminho>).
      </>
    ),
  }

  const passosWindows: Passo[] = [
    {
      titulo: 'Instalar o aplicativo no totem',
      corpo: (
        <>
          No computador do totem, baixe e instale o <Caminho>Totem Autoatendimento</Caminho>. É o mesmo instalador
          para os dois modos, quem define o que o aparelho faz é o código do passo 3.
          <Stack spacing={2} sx={{ mt: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              href={URL_DOWNLOADS}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              Baixar o instalador
            </Button>

            <CampoCopiavel
              rotulo="Endereço para download"
              valor={URL_DOWNLOADS}
              ajuda="Se estiver configurando de outro computador, abra este endereço no totem."
            />
          </Stack>
        </>
      ),
    },
    {
      titulo: 'Abrir o aplicativo',
      corpo: (
        <>
          Abra o <Caminho>PDV Sischef Totem</Caminho> no aparelho. Ele já abre na tela de pareamento, pedindo um
          código, não há usuário nem senha a digitar.
        </>
      ),
    },
    passoCodigo,
    passoPronto,
  ]

  const passosAndroid: Passo[] = [
    {
      titulo: 'Instalar o aplicativo no totem',
      corpo: (
        <>
          No totem Android, baixe e instale o <Caminho>Sischef Totem</Caminho>. Se o Android avisar sobre a origem
          do arquivo, permita a instalação.
          <Stack spacing={2} sx={{ mt: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              href={URL_DOWNLOADS}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              Baixar o aplicativo
            </Button>

            <CampoCopiavel
              rotulo="Endereço para download"
              valor={URL_DOWNLOADS}
              ajuda="Abra este endereço no navegador do próprio totem para baixar o aplicativo."
            />
          </Stack>
          <Box sx={{ mt: 1 }}>
            Vai cobrar com cartão? O app de TEF da <Caminho>Elgin</Caminho> também precisa estar no totem. Em muitos
            modelos ele já vem instalado de fábrica.
          </Box>
        </>
      ),
    },
    {
      titulo: 'Abrir o aplicativo',
      corpo: (
        <>
          Abra o <Caminho>Sischef Totem</Caminho>. Ele já abre na tela de pareamento, pedindo um código, não há
          usuário nem senha a digitar. Se o totem tem impressora, deixe-a ligada: quando o Android perguntar se o
          Sischef Totem pode usá-la, marque para usar sempre e confirme.
        </>
      ),
    },
    passoCodigo,
    {
      titulo: 'Ativar a maquininha',
      corpo: (
        <>
          Só se o totem vai cobrar com cartão. No aparelho: <Caminho>Sobre</Caminho> →{' '}
          <Caminho>Configurações</Caminho> (pede o PIN da loja) → <Caminho>Ativar a maquininha deste totem</Caminho>.
          É feito uma vez por aparelho.
        </>
      ),
    },
    {
      titulo: 'Travar o totem',
      corpo: (
        <>
          Ainda em <Caminho>Configurações</Caminho>, toque em <Caminho>Entrar em modo kiosk</Caminho> e aceite
          quando o Android perguntar se o Sischef Totem deve ser a tela inicial. Assim o totem volta sozinho se
          alguém apertar o botão de início ou abrir outro app, e abre sozinho quando o aparelho liga. Os botões do
          Android continuam na tela: para escondê-los de vez, o aparelho precisa ser preparado pelo suporte.
        </>
      ),
    },
    passoPronto,
  ]

  function blocoCodigo(): ReactNode {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )
    }

    if (error) {
      return (
        <Alert
          severity="error"
          action={
            identificador && (
              <Button size="small" color="inherit" onClick={() => void gerar(identificador.id)}>
                Tentar de novo
              </Button>
            )
          }
        >
          Não foi possível gerar o código. Só pontos de acesso do tipo totem podem ser pareados.
        </Alert>
      )
    }

    if (!data) return null

    return (
      <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderRadius: 2,
            border: 1,
            borderColor: expirado ? 'divider' : 'primary.main',
            bgcolor: 'action.hover',
            alignSelf: 'stretch',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontSize: 'clamp(2rem, 8vw, 3rem)',
              fontWeight: 700,
              letterSpacing: '0.1em',
              lineHeight: 1.2,
              color: expirado ? 'text.disabled' : 'text.primary',
              textDecoration: expirado ? 'line-through' : 'none',
            }}
          >
            {formatarCodigo(data.codigo)}
          </Typography>

          <Typography variant="body1" color={expirado ? 'error' : 'text.secondary'}>
            {expirado ? 'Código expirado — gere outro' : `Expira em ${formatarRestante(restanteMs)}`}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => void copiar()}
            disabled={expirado}
            fullWidth
            sx={{ textTransform: 'none' }}
          >
            Copiar código
          </Button>

          <Button
            variant={expirado ? 'contained' : 'outlined'}
            startIcon={<RefreshIcon />}
            onClick={() => identificador && void gerar(identificador.id)}
            fullWidth
            sx={{ textTransform: 'none' }}
          >
            Gerar novo código
          </Button>
        </Stack>
      </Stack>
    )
  }

  return (
    <>
      <Dialog open={aberto} onClose={aoFechar} fullWidth maxWidth="sm" fullScreen={telaEstreita}>
        <DialogTitle sx={{ pb: 1 }} onClose={aoFechar}>
          <Typography variant="h5" component="span">
            Configurar totem
          </Typography>

          <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
            {identificador?.numero?.trim() || (identificador ? ROTULO_TIPO_IDENTIFICADOR[identificador.tipo] : '')}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {ehCheckout ? (
                <>
                  Este é o <strong>totem de checkout</strong>: o cliente informa a comanda ou mesa, confere o que
                  consumiu e paga no TEF do próprio aparelho. Fica normalmente na saída do salão.
                </>
              ) : (
                <>
                  Este é o <strong>totem de pedidos</strong>: o cliente monta o pedido sozinho e o envia para a
                  produção. Fica normalmente na entrada ou perto do balcão.
                </>
              )}
            </Typography>
          </Alert>

          {/* A pergunta do aparelho vem ANTES do roteiro: o passo a passo muda com ela. */}
          {!plataforma ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Qual é o aparelho deste totem? O jeito de instalar muda em cada um.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <BotaoPlataforma
                  icone={<DesktopWindowsIcon sx={{ fontSize: 34 }} />}
                  titulo="Totem Windows"
                  descricao="Computador ou totem com Windows"
                  aoEscolher={() => setPlataforma('WINDOWS')}
                />

                <BotaoPlataforma
                  icone={<AndroidIcon sx={{ fontSize: 34 }} />}
                  titulo="Totem Android"
                  descricao="Totem ou tablet com Android"
                  aoEscolher={() => setPlataforma('ANDROID')}
                />
              </Stack>
            </Stack>
          ) : (
            <>
              <Button
                size="small"
                color="inherit"
                startIcon={<ArrowBackIcon />}
                onClick={() => setPlataforma(null)}
                sx={{ mb: 1, textTransform: 'none' }}
              >
                {ROTULO_PLATAFORMA[plataforma]} — trocar
              </Button>

              <Stepper orientation="vertical" activeStep={-1} nonLinear>
                {(plataforma === 'ANDROID' ? passosAndroid : passosWindows).map((passo) => (
                  <Step key={passo.titulo} active expanded>
                    <StepLabel>
                      <Typography variant="subtitle2" component="span">
                        {passo.titulo}
                      </Typography>
                    </StepLabel>

                    <StepContent>
                      <Typography variant="body2" color="text.secondary" component="div">
                        {passo.corpo}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>

              {plataforma === 'ANDROID' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <AlertTitle>No Android</AlertTitle>
                  <Typography variant="body2" component="div">
                    A balança não funciona, e o totem só abre sozinho quando o aparelho liga se estiver em modo kiosk.
                  </Typography>
                </Alert>
              )}

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                  Parear um aparelho novo neste ponto de acesso <strong>desvincula o anterior</strong>. Precisa
                  de mais de um totem? <Caminho>Crie um ponto de acesso para cada um.</Caminho>
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            size="large"
            onClick={aoFechar}
            startIcon={<CloseIcon />}
            sx={{ textTransform: 'none' }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copiado}
        autoHideDuration={2000}
        onClose={() => setCopiado(false)}
        message="Código copiado"
      />
    </>
  )
}
